import { ConversationTagWithId } from "@redotech/redo-model/conversation";
import { TagGroup } from "@redotech/redo-model/support/tag-group-zod-schema";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
} from "./conversation-tag-pill";

export interface GroupedTagsData {
  groups: Array<{ group: TagGroup; tags: ConversationTagWithId[] }>;
  ungrouped: ConversationTagWithId[];
}

interface TagGroupMaps {
  tagToGroupMap: Map<string, TagGroup>;
  groupNamesMap: Map<string, string>;
}

/**
 * Creates lookup maps for tag groups - now works with tags that have tagGroupId
 */
export function createTagGroupMaps(
  tagGroups: TagGroup[],
  allTags: ConversationTagWithId[],
): TagGroupMaps {
  const tagToGroupMap = new Map<string, TagGroup>();
  const groupNamesMap = new Map<string, string>();
  const groupIdMap = new Map<string, TagGroup>();

  for (const group of tagGroups) {
    groupNamesMap.set(group._id, group.name);
    groupIdMap.set(group._id, group);
  }

  for (const tag of allTags) {
    if ((tag as any).tagGroupId) {
      const group = groupIdMap.get((tag as any).tagGroupId);
      if (group) {
        tagToGroupMap.set(tag.tagId, group);
      }
    }
  }

  return { tagToGroupMap, groupNamesMap };
}

/**
 * Groups conversation tags by their assigned tag groups
 */
export function groupTagsByTagGroups(
  tags: ConversationTagWithId[],
  tagGroups: TagGroup[] | undefined,
): GroupedTagsData {
  if (!tagGroups || tags.length === 0) {
    return { groups: [], ungrouped: tags };
  }

  const groups: Array<{ group: TagGroup; tags: ConversationTagWithId[] }> = [];
  const ungrouped: ConversationTagWithId[] = [];

  const { tagToGroupMap } = createTagGroupMaps(tagGroups, tags);

  for (const tag of tags) {
    const group = tagToGroupMap.get(tag.tagId);
    if (group) {
      // Find existing group entry or create new one
      let groupEntry = groups.find((g) => g.group._id === group._id);
      if (!groupEntry) {
        groupEntry = { group, tags: [] };
        groups.push(groupEntry);
      }
      groupEntry.tags.push(tag);
    } else {
      ungrouped.push(tag);
    }
  }

  return { groups, ungrouped };
}

/**
 * Creates RedoListItem array with grouped tags and section headers for dropdowns
 */
export function createGroupedTagListItems<
  T extends ConversationTagWithId | "create",
>(
  tags: ConversationTagWithId[],
  tagGroups: TagGroup[] | undefined,
  options: {
    createTagMenuItem?: RedoListItem<T>;
    onTagEdit?: (tag: ConversationTagWithId) => void;
    includeCreateValue?: boolean;
  } = {},
): RedoListItem<T>[] {
  const tagItems: RedoListItem<T>[] = [];

  if (tags.length > 0 && tagGroups) {
    const { tagToGroupMap, groupNamesMap } = createTagGroupMaps(
      tagGroups,
      tags,
    );

    const groupedTags = new Map<string, ConversationTagWithId[]>();
    const ungroupedTags: ConversationTagWithId[] = [];

    for (const tag of tags) {
      const group = tagToGroupMap.get(tag.tagId);
      if (group) {
        const groupId = group._id;
        if (!groupedTags.has(groupId)) {
          groupedTags.set(groupId, []);
        }
        groupedTags.get(groupId)!.push(tag);
      } else {
        ungroupedTags.push(tag);
      }
    }

    if (ungroupedTags.length > 0) {
      if (groupedTags && groupedTags.size > 0) {
        tagItems.push({
          id: "general-section",
          value: (options.includeCreateValue
            ? "create"
            : ungroupedTags[0]) as T,
          type: "section",
          text: "General",
        });
      }

      for (const tag of ungroupedTags) {
        tagItems.push({
          id: tag.tagId,
          value: tag as T,
          type: "badge",
          badge: {
            text: tag.name,
            color: BadgePillThemeToColorMapping[tag.pillTheme],
            type: BadgePillThemeToTypeMapping[tag.pillTheme],
          },
          menu: options.onTagEdit
            ? { onClick: () => options.onTagEdit!(tag) }
            : undefined,
        });
      }
    }

    for (const [groupId, groupTags] of groupedTags) {
      const groupName = groupNamesMap.get(groupId) || "Unknown Group";

      tagItems.push({
        id: `${groupId}-section`,
        value: (options.includeCreateValue ? "create" : groupTags[0]) as T,
        type: "section",
        text: groupName,
      });

      for (const tag of groupTags) {
        tagItems.push({
          id: tag.tagId,
          value: tag as T,
          type: "badge",
          badge: {
            text: tag.name,
            color: BadgePillThemeToColorMapping[tag.pillTheme],
            type: BadgePillThemeToTypeMapping[tag.pillTheme],
          },
          menu: options.onTagEdit
            ? { onClick: () => options.onTagEdit!(tag) }
            : undefined,
        });
      }
    }
  } else if (tags.length > 0) {
    for (const tag of tags) {
      tagItems.push({
        id: tag.tagId,
        value: tag as T,
        type: "badge",
        badge: {
          text: tag.name,
          color: BadgePillThemeToColorMapping[tag.pillTheme],
        },
        menu: options.onTagEdit
          ? { onClick: () => options.onTagEdit!(tag) }
          : undefined,
      });
    }
  }

  if (options.createTagMenuItem) {
    tagItems.push(options.createTagMenuItem);
  }

  return tagItems;
}
